import type { FC } from "react";
import { useState, useRef, useEffect } from "react";
import {
  TextOrHTML,
  Button,
  Container,
  Text,
  Checkbox,
} from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { ScrollableText } from "../ScrollableText";
import type { UnderwriterLegalAgreementsProps, Question } from "./types";
import { useTokensValues } from "../../hooks/tokens";
import { useSendLegalAgreementsSms } from "../../hooks/useSendLegalAgreementsSms";

export const UnderwriterLegalAgreements: FC<
  UnderwriterLegalAgreementsProps
> = props => {
  const {
    pageTitle,
    questions,
    carrierLegalAgreements,
    previousStep,
    currentPagePath,
    journeyToken,
    children,
    className,
    customerPhoneNumber,
    backofficeSmsEnabled,
  } = props;

  const classes = classNames(
    "mobius",
    "mobius-underwriter-legal-agreements",
    className,
  );
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [isCarrierAgreed, setIsCarrierAgreed] = useState<boolean>(false);
  const [isCommonAgreed, setIsCommonAgreed] = useState<boolean>(false);

  // Add useEffect to automatically check MainCheckbox when both are checked
  useEffect(() => {
    setIsCommonAgreed(isAgreed && isCarrierAgreed);
  }, [isAgreed, isCarrierAgreed]);

  const formattedQuestions = (questionsList: Question[] | undefined) => {
    const formatQuestions = questionsList?.map((question, index) => {
      return (
        <li key={index}>
          <h2 className="mobius-underwriter-legal-agreements__section-title">
            {question.primaryText}
          </h2>
          <TextOrHTML text={question.secondaryText} />
        </li>
      );
    });
    return (
      <ul className="mobius-underwriter-legal-agreements__bullet-list">
        {formatQuestions}
      </ul>
    );
  };

  const _handleLegalAgreementClick = () => {
    if (legalAgreementRef.current) {
      legalAgreementRef.current.click();
    }
  };

  const _handleCarrierAgreementClick = () => {
    if (carrierAgreementRef.current) {
      carrierAgreementRef.current.click();
    }
  };
  const authenticityToken = useTokensValues().authenticityToken;
  const canProceed = isAgreed && isCarrierAgreed;
  const formRef = useRef<HTMLFormElement>(null);
  const legalAgreementRef = useRef<HTMLInputElement>(null);
  const carrierAgreementRef = useRef<HTMLInputElement>(null);
  const commmonAgreementRef = useRef<HTMLInputElement>(null);
  const { message, handleSendSms, isError, isLoading } =
    useSendLegalAgreementsSms();
  const messageClass = classNames(
    "mobius-underwriter-legal-agreements__sms-message",
    {
      "--is-invalid": isError,
    },
  );

  return (
    <div>
      <Text
        className="mobius-underwriter-legal-agreements__page-title"
        variant="h1"
      >
        {pageTitle || "Let's review some legal stuff"}
      </Text>
      <div className={classes}>
        <div>
          {backofficeSmsEnabled && (
            <>
              {message && <div className={messageClass}>{message}</div>}
              <Button
                onClick={() => {
                  void handleSendSms();
                }}
                className="mobius-underwriter-legal-agreements__sms-button"
                isLoading={isLoading}
              >
                {`Send Legal Agreements SMS to: ${customerPhoneNumber}`}
              </Button>
            </>
          )}
          <form ref={formRef} action={currentPagePath} method="POST">
            <input
              type="hidden"
              name="authenticity_token"
              value={authenticityToken}
            />
            <input type="hidden" name="journey_token" value={journeyToken} />

            <Container className="mobius-underwriter-legal-agreements__page-container">
              <Container className="mobius-underwriter-legal-agreements__legal-agreements-column">
                <ScrollableText>{formattedQuestions(questions)}</ScrollableText>
                <Checkbox
                  label="By checking the box I am acknowledging that I have read and agree to each of the above statements"
                  onChange={e => setIsAgreed(e.target.checked)}
                />
                <h2 className="mobius-underwriter-legal-agreements__carrier-title">
                  Carrier legal agreements
                </h2>
                <ScrollableText style={{ paddingLeft: "20px" }}>
                  <div className="mobius-underwriter-legal-agreements__agreement-content">
                    <TextOrHTML text={carrierLegalAgreements ?? ""} />
                  </div>
                </ScrollableText>
                <Checkbox
                  label="By checking the box I am acknowledging that I have read and agree to each of the above statements"
                  onChange={e => setIsCarrierAgreed(e.target.checked)}
                />
                <input
                  className="mobius-underwriter-legal-agreements__main-checkbox"
                  ref={commmonAgreementRef}
                  type="checkbox"
                  id="common-agreement"
                  name="common_agreement[]"
                  checked={isCommonAgreed}
                  onChange={e => setIsCommonAgreed(e.target.checked)}
                  value="By checking the box I am acknowledging that I have read and agree to each of the above statements"
                />
                <Container className="mobius-underwriter-legal-agreements__form-actions">
                  <Button
                    size="md"
                    elementType="a"
                    href={previousStep}
                    className="qa-back-button"
                    id="BackButton"
                    variant="secondary"
                    type="button"
                  >
                    Back
                  </Button>
                  <Button
                    size="md"
                    variant="primary"
                    type="submit"
                    isDisabled={!canProceed}
                    className="qa-next-button js-next-button"
                    id="NextButton"
                  >
                    Next step
                  </Button>
                </Container>
              </Container>
            </Container>
          </form>
        </div>
        {children}
      </div>
    </div>
  );
};

UnderwriterLegalAgreements.displayName = "UnderwriterLegalAgreements";
